import dayjs from 'lib/dayjs';
import Icon from 'components/AppIcon';

const getPriority = (days) => {
  if (days === null) return null;
  if (days <= 0)
    return {
      label: 'Overdue',
      color: 'bg-red-100 text-red-700 border-red-200',
      icon: 'AlertTriangle',
    };
  if (days <= 15)
    return { label: 'High', color: 'bg-red-100 text-red-700 border-red-200', icon: 'ArrowUp' };
  if (days <= 30)
    return {
      label: 'Medium',
      color: 'bg-amber-100 text-amber-700 border-amber-200',
      icon: 'Minus',
    };
  if (days <= 60)
    return { label: 'Low', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: 'ArrowDown' };
  return { label: 'Low', color: 'bg-green-100 text-green-700 border-green-200', icon: 'ArrowDown' };
};

const getStatusBadge = (status) => {
  const map = {
    ACTIVE: { label: 'Active', color: 'bg-green-100 text-green-700' },
    PENDING: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
    EXPIRED: { label: 'Expired', color: 'bg-red-100 text-red-700' },
    COMPLETED: { label: 'Completed', color: 'bg-blue-100 text-blue-700' },
    BREACHED: { label: 'Breached', color: 'bg-rose-100 text-rose-700' },
    CANCELLED: { label: 'Cancelled', color: 'bg-gray-100 text-gray-700' },
  };
  return map[status] || { label: status, color: 'bg-gray-100 text-gray-700' };
};

const UpcomingCard = ({ bond }) => {
  const days = dayjs(bond?.end_date).diff(dayjs(), 'day') ?? null;
  const priority = getPriority(days);
  const statusBadge = getStatusBadge(bond.status);
  const userName = bond.user ? `${bond.user.first_name} ${bond.user.last_name}` : 'N/A';
  const deptName = bond.user?.department?.name || 'N/A';

  return (
    <div className="space-y-3">
      {/* Header row */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-bold text-primary-700">
            {bond.user?.first_name?.[0]}
            {bond.user?.last_name?.[0]}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 truncate">{userName}</p>
          <p className="text-xs text-gray-500">
            {bond.user?.employee_id || 'N/A'} · {deptName}
          </p>
        </div>
      </div>

      {/* Dates */}
      <div className="space-y-1">
        <div className="flex items-center gap-1.5 text-xs text-gray-600">
          <Icon name="Calendar" size={12} />
          <span>{dayjs(bond.start_date).format('DD MMM YYYY')}</span>
        </div>
        {bond.end_date && (
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <Icon name="Calendar" size={12} />
            <span>→ {dayjs(bond.end_date).format('DD MMM YYYY')}</span>
          </div>
        )}
      </div>

      {/* Days remaining - only show within 60 days */}
      {days !== null && (
        <div className="flex items-center gap-1.5">
          <Icon name="Clock" size={14} className={days <= 0 ? 'text-red-500' : 'text-gray-500'} />
          <span className={`text-xs font-semibold ${days <= 0 ? 'text-red-600' : 'text-gray-700'}`}>
            {days <= 0 ? `${Math.abs(days)} days overdue` : `${days} days remaining`}
          </span>
        </div>
      )}

      {/* Badges */}
      <div className="flex items-center gap-2 flex-wrap">
        {priority && (
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${priority.color}`}
          >
            <Icon name={priority.icon} size={10} />
            {priority.label}
          </span>
        )}
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge.color}`}>
          {statusBadge.label}
        </span>
      </div>
    </div>
  );
};

const InProgressCard = ({ bond }) => {
  const userName = bond.user ? `${bond.user.first_name} ${bond.user.last_name}` : 'N/A';
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-bold text-blue-700">
            {bond.user?.first_name?.[0]}
            {bond.user?.last_name?.[0]}
          </span>
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">{userName}</p>
          <p className="text-xs text-gray-500">{bond.user?.employee_id}</p>
        </div>
      </div>
      <div className="space-y-1">
        <div className="flex items-center gap-1.5 text-xs text-gray-600">
          <Icon name="Calendar" size={12} />
          <span>{dayjs(bond.start_date).format('DD MMM YYYY')}</span>
        </div>
        {bond.end_date && (
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <Icon name="Calendar" size={12} />
            <span>→ {dayjs(bond.end_date).format('DD MMM YYYY')}</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1.5 text-xs text-gray-600">
        <Icon name="RefreshCw" size={12} />
        <span>Updated {dayjs(bond.updated_at).format('DD MMM')}</span>
      </div>
      <div className="text-xs text-gray-500 italic border-l-2 border-blue-300 pl-2">
        Renewal in progress
      </div>
    </div>
  );
};

const OnHoldCard = ({ bond }) => {
  const userName = bond.user ? `${bond.user.first_name} ${bond.user.last_name}` : 'N/A';
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-bold text-rose-700">
            {bond.user?.first_name?.[0]}
            {bond.user?.last_name?.[0]}
          </span>
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">{userName}</p>
          <p className="text-xs text-gray-500">{bond.user?.employee_id}</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 text-xs text-gray-600">
        <Icon name="PauseCircle" size={12} />
        <span>On hold since {dayjs(bond.updated_at).format('DD MMM YYYY')}</span>
      </div>
      <div className="text-xs text-gray-500 italic border-l-2 border-rose-300 pl-2">
        Awaiting resolution
      </div>
    </div>
  );
};

const DoneCard = ({ bond }) => {
  const userName = bond.user ? `${bond.user.first_name} ${bond.user.last_name}` : 'N/A';
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-bold text-emerald-700">
            {bond.user?.first_name?.[0]}
            {bond.user?.last_name?.[0]}
          </span>
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">{userName}</p>
          <p className="text-xs text-gray-500">{bond.user?.employee_id}</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 text-xs text-emerald-600">
        <Icon name="CheckCircle" size={12} />
        <span>Updated on {dayjs(bond.updated_at).format('DD MMM YYYY')}</span>
      </div>
      {bond.start_date && bond.end_date && (
        <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-2 border border-gray-100">
          <p className="font-medium text-gray-700 mb-1">Bond Period</p>
          <p>
            {dayjs(bond.start_date).format('DD MMM YYYY')} →{' '}
            {dayjs(bond.end_date).format('DD MMM YYYY')}
          </p>
        </div>
      )}
    </div>
  );
};

const CARD_MAP = {
  UPCOMING: UpcomingCard,
  IN_PROGRESS: InProgressCard,
  ON_HOLD: OnHoldCard,
  DONE: DoneCard,
};

const BondCard = ({ bond, columnId, isDragging, onClick, isSelected, isMultiSelectMode }) => {
  const CardComponent = CARD_MAP[columnId] || UpcomingCard;

  return (
    <div
      onClick={onClick}
      className={`bg-white border rounded-md p-4 cursor-pointer transition-all duration-200 hover:shadow-md ${
        isDragging ? 'shadow-lg rotate-2 scale-105' : 'shadow-sm'
      } ${
        isSelected
          ? 'ring-2 ring-primary border-primary'
          : 'border-gray-200 hover:border-primary-300'
      } ${isMultiSelectMode ? 'hover:ring-2 hover:ring-primary-300' : ''}`}
    >
      <CardComponent bond={bond} />
    </div>
  );
};

export default BondCard;
